import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Minus } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";

const SPECS = [
  { match: "à piles", usage: "Individuel", conn: "—", alim: "Piles AA", ead: false, ideal: "Contrôles occasionnels, véhicules de service" },
  { match: "électronique professionnel rechargeable", usage: "Individuel", conn: "—", alim: "USB-C rechargeable", ead: false, ideal: "Auto-contrôle avant conduite ou prise de poste" },
  { match: "Bluetooth", usage: "Individuel / HSE", conn: "Bluetooth + appli mobile", alim: "USB rechargeable", ead: false, ideal: "Services HSE : historique et export des tests" },
  { match: "ALC-308", usage: "Flotte / véhicule", conn: "RS232 → MDVR", alim: "DC 5–24 V", ead: false, ideal: "Flottes équipées MDVR / dashcam" },
  { match: "GPS", usage: "Flotte / véhicule", conn: "RS232 + GPS", alim: "DC 5–24 V", ead: false, ideal: "Traçabilité géolocalisée des tests" },
  { match: "anti-démarrage", usage: "Flotte / véhicule", conn: "RS232 → MDVR", alim: "DC 5–24 V", ead: true, ideal: "Verrouillage du démarrage (transport, BTP)" },
  { match: "reconnaissance faciale (prise de poste)", usage: "Site / prise de poste", conn: "Cloud 4G / Wi-Fi", alim: "Secteur", ead: false, ideal: "Contrôles nominatifs systématiques" },
  { match: "3-en-1", usage: "Site / prise de poste", conn: "Cloud 4G / Wi-Fi", alim: "Secteur", ead: false, ideal: "Sites sensibles : alcool + température + identité" },
];

const ROWS = [
  ["Usage", (s) => s.usage],
  ["Connectivité", (s) => s.conn],
  ["Alimentation", (s) => s.alim],
  ["Anti-démarrage (EAD)", (s) => (s.ead ? <Check size={18} style={{ color: "var(--orange)" }} /> : <Minus size={16} style={{ color: "var(--ink-2)" }} />)],
  ["Idéal pour", (s) => s.ideal],
];

export default function ComparateurEthylotests() {
  const [items, setItems] = useState([]);
  const [pack, setPack] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/products").then((r) => {
      const ethylos = r.data.filter((p) => p.subcategory === "Éthylotests & prévention");
      const rows = [];
      for (const spec of SPECS) {
        const prod = ethylos.find((p) => p.name.toLowerCase().includes(spec.match.toLowerCase()));
        if (prod) rows.push({ ...spec, prod });
      }
      setItems(rows);
      setPack(ethylos.find((p) => p.name.startsWith("Pack Prévention")));
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="comparateur-page" className="pt-[148px]">
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>Guide de choix</p></Reveal>
        <Reveal delay={0.05}>
          <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tight leading-none">Comparateur d'éthylotests</h1>
          <p className="mt-6 text-lg max-w-2xl" style={{ color: "var(--ink-2)" }}>
            Tous nos éthylotests utilisent un capteur électrochimique de précision professionnelle, certifié CE/RoHS. Choisissez le modèle adapté à votre usage.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 overflow-x-auto" data-lenis-prevent style={{ border: "1px solid var(--border)" }} data-testid="compare-table-wrapper">
            <table className="w-full text-sm" style={{ minWidth: `${180 + items.length * 190}px`, borderCollapse: "collapse" }} data-testid="compare-table">
              <thead>
                <tr>
                  <th className="p-4 text-left align-bottom sticky left-0 z-10" style={{ background: "var(--white)", borderBottom: "2px solid var(--orange)", minWidth: "160px" }}>
                    <span className="overline" style={{ color: "var(--ink-2)" }}>Critères</span>
                  </th>
                  {items.map((it) => (
                    <th key={it.prod.id} className="p-4 text-left align-bottom" style={{ background: "var(--white)", borderBottom: "2px solid var(--orange)", minWidth: "190px" }}>
                      <Link to={`/catalogue/${it.prod.id}`} className="block group" data-testid={`compare-head-${it.prod.id}`}>
                        <img src={it.prod.image} alt={it.prod.name} className="w-20 h-20 object-cover mb-3 transition-transform duration-500 group-hover:scale-105" style={{ border: "1px solid var(--border)" }} />
                        <span className="font-display font-bold leading-tight block">{it.prod.name}</span>
                        <span className="font-display font-black text-lg mt-1 block" style={{ color: "var(--orange)" }}>
                          Sur devis
                        </span>
                      </Link>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map(([label, fn], ri) => (
                  <tr key={label} style={{ background: ri % 2 ? "var(--white)" : "var(--bone)" }}>
                    <td className="p-4 font-semibold sticky left-0 z-10" style={{ background: ri % 2 ? "var(--white)" : "var(--bone)", borderRight: "1px solid var(--border)" }}>{label}</td>
                    {items.map((it) => (
                      <td key={it.prod.id} className="p-4" style={{ color: "var(--ink-2)" }}>{fn(it)}</td>
                    ))}
                  </tr>
                ))}
                <tr style={{ background: "var(--white)" }}>
                  <td className="p-4 sticky left-0 z-10" style={{ background: "var(--white)" }} />
                  {items.map((it) => (
                    <td key={it.prod.id} className="p-4">
                      <Link to={`/catalogue/${it.prod.id}`} className="btn-outline px-4 py-2 text-xs font-semibold inline-flex items-center gap-1" data-testid={`compare-cta-${it.prod.id}`}>
                        Voir la fiche <ArrowRight size={13} />
                      </Link>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </Reveal>

        {pack && (
          <Reveal delay={0.15}>
            <Link to={`/catalogue/${pack.id}`} className="mt-10 flex flex-col md:flex-row items-center gap-6 p-8 group" style={{ background: "var(--ink)", color: "var(--bone)" }} data-testid="compare-pack-banner">
              <img src={pack.image} alt={pack.name} className="w-28 h-28 object-cover shrink-0" />
              <div className="flex-1">
                <span className="overline px-2 py-1" style={{ background: "var(--orange)", color: "var(--bone)" }}>Pack −20%</span>
                <h2 className="font-display font-black text-2xl mt-3">{pack.name}</h2>
                <p className="mt-2 text-sm" style={{ color: "rgba(245,245,240,0.7)" }}>{pack.short}</p>
              </div>
              <span className="font-display font-black text-3xl whitespace-nowrap" style={{ color: "var(--orange)" }}>Sur devis</span>
            </Link>
          </Reveal>
        )}

        <p className="mt-8 text-sm" style={{ color: "var(--ink-2)" }}>
          Besoin d'aide pour dimensionner votre déploiement ? <Link to="/contact" className="font-semibold link-hover" style={{ color: "var(--orange)" }}>Demandez conseil à nos préventeurs</Link> — n'oubliez pas les <Link to="/catalogue" className="font-semibold link-hover" style={{ color: "var(--orange)" }}>embouts hygiéniques</Link> pour vos campagnes de dépistage.
        </p>
      </section>
    </div>
  );
}
